import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Building,
  MessageSquare,
  Settings,
  LogOut,
  Bell,
} from 'lucide-react';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from '../ui/sidebar';
import { supabase } from '../../lib/supabaseClient';
import ThemeToggle from '../ThemeToggle';

// Nav items configuration
const navItems = [
  { to: '/recruiter', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { to: '/recruiter/candidates', icon: Users, label: 'Candidates' },
  { to: '/recruiter/rfqs', icon: Briefcase, label: 'RFQs' },
  { to: '/recruiter/clients', icon: Building, label: 'Clients' },
  { to: '/recruiter/requests', icon: MessageSquare, label: 'Messages' },
];

const settingsItems = [
  { to: '/recruiter/settings', icon: Settings, label: 'Settings' },
];

// App Sidebar Component
function AppSidebar() {
  const { user, signOut } = useSupabaseAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpenMobile } = useSidebar();
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread messages count
  useEffect(() => {
    if (!user) return;

    const fetchUnreadCount = async () => {
      try {
        // Fetch all requests with last read timestamp
        const { data: requests } = await supabase
          .from('candidate_requests')
          .select('id, last_read_by_recruiter_at');

        if (!requests || requests.length === 0) {
          setUnreadCount(0);
          return;
        }

        // Count unread messages for each request
        let totalUnread = 0;

        for (const request of requests) {
          const query = supabase
            .from('request_messages')
            .select('*', { count: 'exact', head: true })
            .eq('request_id', request.id)
            .eq('sender_type', 'client');

          // If there's a last read timestamp, only count messages after that
          if (request.last_read_by_recruiter_at) {
            query.gt('created_at', request.last_read_by_recruiter_at);
          }

          const { count } = await query;
          totalUnread += (count || 0);
        }

        setUnreadCount(totalUnread);
      } catch (error) {
        console.error('Error fetching unread messages:', error);
      }
    };

    fetchUnreadCount();

    // Set up real-time subscription for new messages
    const channel = supabase
      .channel('request_messages_changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'request_messages' },
        () => {
          fetchUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive = (path, exact) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Sidebar>
      {/* Sidebar Header */}
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-2">
          <img
            src="/appsytec-logo.png"
            alt="Appsytec Logo"
            className="h-8 w-auto object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
          <div className="flex flex-col flex-1 text-left text-sm leading-tight">
            <span className="truncate font-semibold">Appsytec</span>
            <span className="truncate text-xs text-sidebar-foreground/70">Recruitment Portal</span>
          </div>
        </div>
      </SidebarHeader>

      {/* Sidebar Content */}
      <SidebarContent>
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarMenu>
            {navItems.map((item) => (
              <SidebarMenuItem key={item.to}>
                <NavLink to={item.to} end={item.exact} onClick={() => setOpenMobile(false)}>
                  {({ isActive: linkIsActive }) => (
                    <SidebarMenuButton isActive={linkIsActive} tooltip={item.label}>
                      <item.icon />
                      <span>{item.label}</span>
                      {item.label === 'Messages' && unreadCount > 0 && (
                        <Badge variant="destructive" className="ml-auto h-5 w-5 flex items-center justify-center p-0 text-xs">
                          {unreadCount}
                        </Badge>
                      )}
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>

        <SidebarSeparator />

        {/* Settings */}
        <SidebarGroup>
          <SidebarGroupLabel>Settings</SidebarGroupLabel>
          <SidebarMenu>
            {settingsItems.map((item) => (
              <SidebarMenuItem key={item.to}>
                <NavLink to={item.to} onClick={() => setOpenMobile(false)}>
                  {({ isActive: linkIsActive }) => (
                    <SidebarMenuButton isActive={linkIsActive} tooltip={item.label}>
                      <item.icon />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      {/* Sidebar Footer */}
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex flex-col gap-2 p-2">
              <div className="flex items-center gap-2 text-sm">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  {user?.email?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex flex-col flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user?.email?.split('@')[0] || 'User'}</span>
                  <span className="truncate text-xs text-sidebar-foreground/70">Recruiter</span>
                </div>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                <LogOut className="h-4 w-4 mr-2" />
                <span>Logout</span>
              </Button>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

// Header Component
function Header() {
  const { user } = useSupabaseAuth();
  const { state } = useSidebar();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-2 border-b bg-background px-4">
      <div className="flex items-center gap-2">
        <SidebarTrigger className="lg:hidden" />
        <h1 className="text-lg font-semibold">Recruiter Dashboard</h1>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {/* Notification Bell */}
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          onClick={() => navigate('/recruiter/notifications')}
          title="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
        <ThemeToggle />
      </div>
    </header>
  );
}

// Main Layout Component
const RecruiterLayout = () => {
  const { user, userRole } = useSupabaseAuth();

  // Initialize push notifications for mobile app
  usePushNotifications(user, userRole);

  return (
    <SidebarProvider defaultOpen={true}>
      <AppSidebar />
      <SidebarInset>
        <Header />
        <div className="flex flex-1 flex-col gap-3 p-3 sm:gap-4 sm:p-4 md:gap-8 md:p-6 lg:p-8 w-full max-w-full overflow-x-hidden">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default RecruiterLayout;
